import { Commodity } from 'types/Load';
import { Undef } from 'types/UtilityTypes';

/**
 * Converts weight to pounds (lbs).
 * Handles conversion from kg to lbs using the standard conversion factor: 1 kg = 2.20462 lbs
 *
 * @param weight - The weight value to convert
 * @param unit - The unit of the weight ('kg' or 'lbs'). Defaults to 'lbs' if not provided
 * @returns The weight in pounds
 */
export function convertWeightToLbs(weight: number, unit?: string): number {
  if (!weight || weight === 0) return 0;
  if (unit?.toLowerCase() === 'kg') {
    return weight * 2.20462;
  }
  // Default to lbs (no conversion needed)
  return weight;
}

/**
 * Safely converts a value to a number, handling NaN from React Hook Form.
 * React Hook Form returns NaN for empty number fields.
 *
 * @param value - The value to convert (may be NaN, undefined, or a valid number)
 * @returns The number value, or 0 if the value is NaN or undefined
 */
export function safeNumber(value: Undef<number>): number {
  if (value === undefined || Number.isNaN(value)) {
    return 0;
  }
  return value;
}

// Missing units mean unknown pallets, not zero. Never count individual items as pallets.
export function getCommodityPalletCount(commodity: Commodity): Undef<number> {
  const isPalletUnit = (unit?: string) =>
    /^(pallets?|skids?)$/i.test(unit?.trim() ?? '');
  const isCount = (value: number | undefined) =>
    value != null && Number.isInteger(value) && value >= 0;

  if (
    isPalletUnit(commodity.handlingUnitTypeName) &&
    isCount(commodity.handlingQuantity)
  ) {
    return commodity.handlingQuantity;
  }
  if (isPalletUnit(commodity.packagingType) && isCount(commodity.quantity)) {
    return commodity.quantity;
  }
  return undefined;
}

/**
 * Checks if a commodity has meaningful data entered.
 * A commodity is considered to have data if it has:
 * - weightTotal > 0, OR
 * - quantity > 0, OR
 * - handlingQuantity > 0
 *
 * This prevents clearing manually entered values when adding empty commodity items.
 *
 * @param commodity - The commodity object to check
 * @returns true if the commodity has any meaningful data, false otherwise
 */
export function hasCommodityData(commodity: Commodity): boolean {
  const weight = commodity.weightTotal || 0;
  const quantity = safeNumber(commodity.quantity);
  const handlingQty = safeNumber(commodity.handlingQuantity);
  return weight > 0 || quantity > 0 || handlingQty > 0;
}

/**
 * Calculates total weight and total pallets from an array of commodities.
 * Converts all weights to lbs before summing to handle mixed units correctly.
 *
 * @param commodities - Array of commodity objects
 * @returns An object containing totalWeight (in lbs) and totalPallets
 */
export function calculateTotals(commodities: Commodity[]): {
  totalWeight: number;
  totalPallets: Undef<number>;
} {
  const totalWeight = commodities.reduce((sum, commodity) => {
    const weightInLbs = convertWeightToLbs(
      commodity.weightTotal || 0,
      commodity.weightUnit
    );
    return sum + weightInLbs;
  }, 0);

  // Only replace the load's extracted/manual total when every item has a known count.
  const totalPallets = commodities.reduce<Undef<number>>((sum, commodity) => {
    const palletCount = getCommodityPalletCount(commodity);
    return sum === undefined || palletCount === undefined
      ? undefined
      : sum + palletCount;
  }, 0);

  return { totalWeight, totalPallets };
}
